import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';

import { BulkUploadResultsModalComponent } from '../components/bulk-upload-results-modal/bulk-upload-results-modal.component';
import { BulkUploadProgressEvent, BulkUploadResponse } from '../interfaces/stock.interface';
import { StockService } from './stock.service';

export type BulkUploadJobStatus = 'running' | 'completed' | 'failed';

export interface ActiveBulkUploadJob {
  readonly jobId: string;
  readonly storeName: string;
  readonly categoryName: string;
  readonly startedAt: Date;
  readonly stage: string;
  readonly processedCount: number;
  readonly totalCount: number;
  readonly percentage: number;
  readonly status: BulkUploadJobStatus;
  readonly finalResponse?: BulkUploadResponse;
  readonly errorMessage?: string;
}

export interface TrackJobMetadata {
  readonly storeName: string;
  readonly categoryName: string;
}

/**
 * Window during which a completed job stays visible in the indicator before being auto-removed.
 * The user can still click "view results" during this window or dismiss manually.
 */
const AUTO_DISMISS_MS = 15_000;

@Injectable({ providedIn: 'root' })
export class BulkUploadJobTrackerService {
  private readonly jobsSubject = new BehaviorSubject<ReadonlyArray<ActiveBulkUploadJob>>([]);

  /**
   * Per-job subscription to the SSE progress stream. Kept separate from the job object
   * itself because the subscription is infra concern — the view never touches it.
   */
  private readonly progressSubs = new Map<string, Subscription>();
  private readonly autoDismissTimers = new Map<string, ReturnType<typeof setTimeout>>();

  readonly activeJobs$: Observable<ReadonlyArray<ActiveBulkUploadJob>> = this.jobsSubject.asObservable();

  constructor(
    private readonly stockService: StockService,
    private readonly dialog: MatDialog,
  ) {}

  trackJob(jobId: string, metadata: TrackJobMetadata): void {
    if (this.getJob(jobId)) return;

    const initial: ActiveBulkUploadJob = {
      jobId,
      storeName: metadata.storeName,
      categoryName: metadata.categoryName,
      startedAt: new Date(),
      stage: 'Starting',
      processedCount: 0,
      totalCount: 0,
      percentage: 0,
      status: 'running',
    };
    this.jobsSubject.next([...this.jobsSubject.value, initial]);

    const subscription = this.stockService.connectBulkUploadProgress(jobId).subscribe({
      next: (event: BulkUploadProgressEvent) => this.applyEvent(jobId, event),
      error: (err: unknown) => this.markFailed(jobId, this.extractMessage(err)),
    });
    this.progressSubs.set(jobId, subscription);
  }

  dismissJob(jobId: string): void {
    this.cleanup(jobId);
    this.jobsSubject.next(this.jobsSubject.value.filter(j => j.jobId !== jobId));
  }

  openResultsFor(jobId: string): void {
    const job = this.getJob(jobId);
    if (!job || job.status !== 'completed' || !job.finalResponse) return;

    const dialogRef = this.dialog.open(BulkUploadResultsModalComponent, {
      data: { response: job.finalResponse },
      width: '800px',
      maxWidth: '90vw',
      disableClose: false,
      autoFocus: false,
    });

    // When the user closes the results modal after a successful upload, reload
    // the page so whichever view they are on (most likely the stock table) shows
    // the freshly imported lots. Failed imports do NOT trigger the reload — the
    // user needs to stay on the current state to inspect the errors surfaced by
    // the backend.
    dialogRef.afterClosed().subscribe(() => {
      if (job.finalResponse?.isSuccess) {
        this.reloadPage();
      }
    });
  }

  /**
   * Extracted so unit tests can spy on the reload trigger without actually
   * reloading the Karma/Jasmine page. Production path is a straight
   * window.location.reload().
   */
  protected reloadPage(): void {
    window.location.reload();
  }

  getJob(jobId: string): ActiveBulkUploadJob | undefined {
    return this.jobsSubject.value.find(j => j.jobId === jobId);
  }

  private applyEvent(jobId: string, event: BulkUploadProgressEvent): void {
    if (event.type === 'progress') {
      this.patchJob(jobId, {
        stage: event.data.stage,
        processedCount: event.data.processedCount,
        totalCount: event.data.totalCount,
        percentage: event.data.percentage,
      });
      return;
    }

    if (event.type === 'completed') {
      this.patchJob(jobId, {
        status: 'completed',
        percentage: 100,
        finalResponse: event.data,
      });
      this.scheduleAutoDismiss(jobId);
    }
  }

  private markFailed(jobId: string, errorMessage: string): void {
    this.patchJob(jobId, {
      status: 'failed',
      errorMessage,
    });
    this.scheduleAutoDismiss(jobId);
  }

  private patchJob(jobId: string, patch: Partial<ActiveBulkUploadJob>): void {
    const current = this.jobsSubject.value;
    const idx = current.findIndex(j => j.jobId === jobId);
    if (idx === -1) return;

    const updated: ActiveBulkUploadJob = { ...current[idx], ...patch };
    const next = [...current];
    next[idx] = updated;
    this.jobsSubject.next(next);
  }

  private scheduleAutoDismiss(jobId: string): void {
    this.clearAutoDismiss(jobId);
    const timer = setTimeout(() => this.dismissJob(jobId), AUTO_DISMISS_MS);
    this.autoDismissTimers.set(jobId, timer);
  }

  private clearAutoDismiss(jobId: string): void {
    const timer = this.autoDismissTimers.get(jobId);
    if (timer) {
      clearTimeout(timer);
      this.autoDismissTimers.delete(jobId);
    }
  }

  private cleanup(jobId: string): void {
    this.progressSubs.get(jobId)?.unsubscribe();
    this.progressSubs.delete(jobId);
    this.clearAutoDismiss(jobId);
  }

  private extractMessage(err: unknown): string {
    if (err instanceof Error) return err.message;
    if (typeof err === 'string') return err;
    return 'Connection interrupted';
  }
}
